import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { BadgeCheck, Paperclip, Plus, Upload } from 'lucide-react';
import {
  CERT_STANDARD_LABEL,
  CERT_STATUS_LABEL,
  type CertificationDto,
  type CertStandard,
} from '@weloze/shared';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDate } from '../lib/format';
import {
  Alert,
  Badge,
  Button,
  EmptyState,
  Field,
  Modal,
  Select,
  Skeleton,
  Textarea,
} from '../components/ui';

/**
 * `/certificados` — os certificados do próprio fornecedor (PLAN §9, fatia i).
 *
 * Até aqui o fornecedor não tinha onde ver nem enviar o que determina 20% da
 * própria nota: o certificado chegava por e-mail e alguém da Qualidade digitava.
 *
 * ## O que esta tela deliberadamente **não** faz
 *
 * Não deixa o fornecedor editar nem apagar certificado já enviado, e não mostra
 * nota nenhuma. O ISQ é **derivado** das normas vigentes na data de corte
 * (`CLAUDE.md`) — se aparecesse aqui um campo de nota, ou um jeito de mexer num
 * certificado já validado, o portal repetiria exatamente a falha da planilha
 * antiga, onde a nota digitada divergia do próprio documento.
 *
 * Enviado pelo fornecedor entra **pendente** e não conta no ISQ até a Qualidade
 * validar. Sem isso bastaria enviar um PDF qualquer para subir a nota.
 */

const ACCEPT = '.pdf,.png,.jpg,.jpeg,.docx,.xlsx,.zip';

export function SupplierCertificationsPage() {
  const { currentStore } = useAuth();
  const storeId = currentStore?.storeId;

  const [items, setItems] = useState<CertificationDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const load = useCallback(async () => {
    if (!storeId) return;
    setError(null);
    try {
      setItems(await api.get<CertificationDto[]>(`/stores/${storeId}/certifications`));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível carregar seus certificados.');
    }
  }, [storeId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!storeId) {
    return (
      <EmptyState
        title="Nenhuma loja vinculada"
        description="Seu usuário ainda não tem loja liberada. Fale com a Qualidade da Weloze."
      />
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Meus certificados
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            São eles que determinam o ISQ — 20% do seu índice geral. A nota não é digitada por
            ninguém: ela sai da combinação de normas vigentes no último dia da competência.
          </p>
        </div>
        <Button onClick={() => setEnviando(true)}>
          <Plus className="size-4" aria-hidden />
          Enviar certificado
        </Button>
      </header>

      {error && <Alert>{error}</Alert>}

      {!items ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-20" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          title="Nenhum certificado enviado"
          description="Sem certificação vigente o ISQ fica em 0. Envie o certificado do seu sistema da qualidade para a Weloze validar."
          action={
            <Button onClick={() => setEnviando(true)}>
              <Plus className="size-4" aria-hidden />
              Enviar certificado
            </Button>
          }
        />
      ) : (
        <ul className="space-y-2">
          {items.map((cert) => (
            <CertificationCard key={cert.id} cert={cert} />
          ))}
        </ul>
      )}

      <SendModal
        open={enviando}
        storeId={storeId}
        onClose={() => setEnviando(false)}
        onDone={() => {
          setEnviando(false);
          void load();
        }}
      />
    </div>
  );
}

function CertificationCard({ cert }: { cert: CertificationDto }) {
  const vencido = cert.daysUntilExpiry < 0;

  return (
    <li className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center gap-2">
        <BadgeCheck
          className={vencido ? 'size-4 text-red-500' : 'size-4 text-emerald-600'}
          aria-hidden
        />
        <span className="font-medium text-slate-900">{CERT_STANDARD_LABEL[cert.standard]}</span>
        {cert.number && <span className="font-mono text-xs text-slate-500">{cert.number}</span>}
        {cert.isIatfRoadmap && <Badge tone="accent">cronograma IATF</Badge>}

        <span className="ml-auto text-sm text-slate-600">
          {vencido ? 'venceu em ' : 'válido até '}
          {formatDate(cert.validUntil)}
        </span>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
        {cert.reviewStatus === 'PENDING' && (
          <Badge tone="warning">
            aguardando validação da Weloze — ainda não conta no ISQ
          </Badge>
        )}
        {cert.reviewStatus === 'REJECTED' && <Badge tone="danger">recusado</Badge>}
        {cert.reviewStatus === 'APPROVED' && !vencido && cert.status === 'VALID' && (
          <Badge tone="success">validado e vigente</Badge>
        )}
        {cert.status !== 'VALID' && <Badge tone="danger">{CERT_STATUS_LABEL[cert.status]}</Badge>}
        {cert.fileId && (
          <a
            href={`/api/files/${cert.fileId}`}
            className="flex items-center gap-1 text-brand-700 underline-offset-4 hover:underline"
          >
            <Paperclip className="size-4" aria-hidden />
            Baixar o arquivo
          </a>
        )}
      </div>

      {/* O motivo da recusa é o que o fornecedor precisa para reenviar certo. */}
      {cert.rejectionReason && (
        <p className="mt-2 rounded-lg border border-red-200 bg-red-50/60 p-2 text-sm text-red-900">
          {cert.rejectionReason}
        </p>
      )}
    </li>
  );
}

/**
 * O envio.
 *
 * O arquivo sobe primeiro e o certificado é criado com o id dele. Se o cadastro
 * falhar, o arquivo fica órfão no acervo — ocupa espaço e nada mais. Na ordem
 * inversa, o certificado nasceria sem o documento que ele afirma existir, e a
 * Qualidade validaria uma linha sem nada anexado.
 */
function SendModal({
  open,
  storeId,
  onClose,
  onDone,
}: {
  open: boolean;
  storeId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [form, setForm] = useState({
    standard: 'ISO_9001' as CertStandard,
    number: '',
    certifyingBody: '',
    validUntil: '',
    notes: '',
  });
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setFields({});
    try {
      const enviado = file
        ? await api.upload<{ id: string }>(`/stores/${storeId}/files`, file)
        : null;

      await api.post(`/stores/${storeId}/certifications`, {
        standard: form.standard,
        number: form.number || null,
        certifyingBody: form.certifyingBody || null,
        validUntil: form.validUntil,
        notes: form.notes || null,
        fileId: enviado?.id ?? null,
      });
      onDone();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.fields ? null : err.message);
        setFields(err.fields ?? {});
      } else {
        setError('Não foi possível enviar o certificado.');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} size="lg" title="Enviar certificado">
      <form onSubmit={submit} className="space-y-4">
        {error && <Alert>{error}</Alert>}

        <Select
          label="Norma"
          name="standard"
          value={form.standard}
          onChange={(e) => setForm({ ...form, standard: e.target.value as CertStandard })}
        >
          {(Object.keys(CERT_STANDARD_LABEL) as CertStandard[]).map((valor) => (
            <option key={valor} value={valor}>
              {CERT_STANDARD_LABEL[valor]}
            </option>
          ))}
        </Select>

        <div className="flex flex-wrap gap-3">
          <Field
            label="Número do certificado"
            name="number"
            className="min-w-[12rem] flex-1"
            value={form.number}
            onChange={(e) => setForm({ ...form, number: e.target.value })}
            error={fields.number}
          />
          <Field
            label="Organismo certificador"
            name="certifyingBody"
            className="min-w-[12rem] flex-1"
            value={form.certifyingBody}
            onChange={(e) => setForm({ ...form, certifyingBody: e.target.value })}
            error={fields.certifyingBody}
          />
        </div>

        <Field
          label="Válido até"
          name="validUntil"
          type="date"
          className="w-52"
          required
          value={form.validUntil}
          onChange={(e) => setForm({ ...form, validUntil: e.target.value })}
          error={fields.validUntil}
          hint="É esta data que o portal compara com o último dia de cada competência."
        />

        <div>
          <p className="text-sm font-medium text-slate-700">Arquivo do certificado</p>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <input
              ref={input}
              type="file"
              accept={ACCEPT}
              className="sr-only"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <Button type="button" variant="outline" size="sm" onClick={() => input.current?.click()}>
              <Upload className="size-4" aria-hidden />
              Escolher arquivo
            </Button>
            {file && <span className="text-sm text-slate-600">{file.name}</span>}
          </div>
          {fields.file && <p className="mt-1 text-sm text-red-600">{fields.file}</p>}
        </div>

        <Textarea
          label="Observações"
          name="notes"
          rows={2}
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
        />

        <p className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 text-sm text-slate-600">
          O certificado entra como <strong>pendente</strong> e não altera o seu ISQ até a Qualidade
          da Weloze validá-lo.
        </p>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" loading={saving}>
            Enviar
          </Button>
        </div>
      </form>
    </Modal>
  );
}
